/**
 * Pick a rotation back up at launch, on the device that holds it.
 *
 * ⚠️ WHAT WAS WRONG. Only the Sign out screen's `mounted` ever resumed a
 * rotation. A member whose commit failed, or who was interrupted mid-walk,
 * relaunched into a Hub full of "being re-encrypted" rows and a Devices screen
 * that said "rotation in progress — read-only" with NO control to get back to
 * the one screen that could finish or cancel it. Reported on 2026-09-10 as
 * "now stuck". The screen said the truth ("You can close the app. This picks
 * up where it left off") and nothing made it true.
 *
 * So the device that holds the pending generation finishes on its own, from
 * wherever the member is, as soon as a status read shows the rotation still
 * pending. Only CONTINUE and COMMIT — the two states that mean "this device's
 * rotation, unfinished". PROMOTE and DROP stay with the screen: they act on the
 * LOCAL generation and must never be decided from a status that has not been
 * read yet, which is also why nothing here moves before `lastRefreshedAt`.
 *
 * Once per generation per launch. A walk that ends ROWS_REMAIN is logged with
 * what is stuck and left for the screen — which the Devices page now offers a
 * way to — rather than retried on every status tick.
 */
import { finishRotation, FINISH_OUTCOME } from './rotation-finish';
import { resumeStateFor, ROTATION_RESUME } from './rotation';

const RESUMABLE = Object.freeze([ROTATION_RESUME.CONTINUE, ROTATION_RESUME.COMMIT]);

/**
 * @param {{ store: object, finish?: typeof finishRotation, decide?: typeof resumeStateFor }} args
 * @returns {() => void} the unwatch
 */
export function installRotationResume({
  store,
  finish = finishRotation,
  decide = resumeStateFor,
} = {}) {
  if (!store) throw new TypeError('installRotationResume needs the root Vuex store');

  /** The pending key id while this launch should finish it, '' otherwise. */
  const signal = () => {
    if (store.getters['courses-keys/isHydrated'] !== true) return '';
    if (store.getters['courses-keys/holdsPendingGeneration'] !== true) return '';
    // A status HAS to have been read: before one, `resumeStateFor` sees no
    // server rotation and answers PROMOTE or DROP — decisions about the local
    // generation that are not this watcher's to make, least of all blind.
    if (!store.state['courses-devices']?.lastRefreshedAt) return '';
    const { action, pendingKeyId } = decide(store);
    return RESUMABLE.includes(action) ? pendingKeyId : '';
  };

  const attempted = new Set();
  const check = pendingKeyId => {
    if (!pendingKeyId || attempted.has(pendingKeyId)) return;
    attempted.add(pendingKeyId);
    finish({ store })
      .then(result => {
        if (result.outcome === FINISH_OUTCOME.ROWS_REMAIN) {
          console.warn(
            `[CoursesE2EE] The rotation could not be finished at launch: ${result.remaining} ` +
              `row(s) still on the old key, ${result.unreachable} unreachable from this device.`,
          );
        }
      })
      .catch(error => {
        console.warn('[CoursesE2EE] The rotation could not be finished at launch:', error);
      });
  };

  check(signal());
  return store.watch(signal, check);
}
